import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Check, ChevronLeft, ChevronRight, MapPin } from 'lucide-react';
import './PublicBooking.css';
import { useToast } from '../shared/ui/Toast/ToastProvider';

// Fase 5.18 (05-remediation.md, parte 2) -- `react-hooks/set-state-in-effect` (hint de React
// Compiler, no un bug de runtime) sube a `error` en todo `src/`. Este archivo hace setState
// síncrono dentro de un `useEffect` en un patrón de sincronización legítimo: resuelve el tipo de booking (cita/llamada) desde `window.location.search` al montar.
// No se reescribe a estado derivado (repetiría el fetch/haría el UI menos predecible en
// varios de estos casos) -- deshabilitado a nivel de archivo, con motivo, no en silencio.
/* eslint-disable react-hooks/set-state-in-effect */
const DAYS_OF_WEEK = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

const PublicBooking = ({ temporalToken = null }) => {
    const { uuid } = useParams();
    const toast = useToast();
    const [step, setStep] = useState(1);
    const [hostInfo, setHostInfo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // Form data
    const [guestName, setGuestName] = useState('');
    const [guestEmail, setGuestEmail] = useState('');
    const [objective, setObjective] = useState('');
    const [selectedDate, setSelectedDate] = useState(null);
    const [selectedTime, setSelectedTime] = useState(null);

    // Calendar state
    const [currentMonth, setCurrentMonth] = useState(new Date(new Date().getFullYear(), new Date().getMonth(), 1));
    const [availability, setAvailability] = useState({}); // { 'YYYY-MM-DD': ['09:00', '09:30', ...] }
    const [duration, setDuration] = useState(30);

    const [bookingType, setBookingType] = useState('cita');
    const [sessionCode, setSessionCode] = useState(null);

    useEffect(() => {
        const search = window.location.search;
        if (search.includes('call')) {
            setBookingType('llamada por whats');
        } else {
            setBookingType('cita');
        }
        
        if (search.includes('call5')) {
            setDuration(5);
        } else if (search.includes('meet5')) {
            setDuration(5);
        } else if (search.includes('meet30')) {
            setDuration(30);
        } else if (search.includes('meet60')) {
            setDuration(60);
        }

        // Extract session code
        if (search.includes('s=')) {
            const urlParams = new URLSearchParams(search);
            setSessionCode(urlParams.get('s'));
        } else {
            const match = search.match(/\/(.+)$/);
            if (match) {
                setSessionCode(match[1]);
            }
        }
    }, []);

    useEffect(() => {
        const fetchHostInfo = async () => {
            try {
                const res = await fetch(`/api/public/booking/${uuid}/info`);
                if (res.ok) {
                    const data = await res.json();
                    setHostInfo(data);
                } else {
                    setError('Usuario no encontrado.');
                }
            } catch (err) {
                setError('Error de conexión.');
            } finally {
                setLoading(false);
            }
        };
        fetchHostInfo();
    }, [uuid]);

    useEffect(() => {
        if (step === 4) {
            fetchAvailability();
        }
    }, [step, currentMonth]);

    const fetchAvailability = async () => {
        const year = currentMonth.getFullYear();
        const month = String(currentMonth.getMonth() + 1).padStart(2, '0');
        const guestTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        try {
            const res = await fetch(`/api/public/booking/${uuid}/availability?month=${year}-${month}&guestTz=${encodeURIComponent(guestTz)}&duration=${duration}`);
            if (res.ok) {
                const data = await res.json();
                setAvailability(data.availability || {});
            }
        } catch (err) {
            console.error("Error fetching availability:", err);
        }
    };

    const handleNext = () => setStep(prev => prev + 1);

    const handleConfirm = async () => {
        try {
            setLoading(true);
            const guestTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
            const res = await fetch(`/api/public/booking/${uuid}/book`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    name: guestName,
                    email: guestEmail,
                    objective,
                    date: selectedDate, // YYYY-MM-DD
                    time: selectedTime, // HH:mm
                    duration,
                    guestTz,
                    temporalToken,
                    sessionCode
                })
            });
            if (res.ok) {
                setStep(5);
            } else {
                toast.error('Hubo un error al agendar. Por favor, intenta de nuevo.');
            }
        } catch (err) {
            toast.error('Error de red al agendar.');
        } finally {
            setLoading(false);
        }
    };

    const prevMonth = () => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
    const nextMonth = () => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));

    const renderCalendar = () => {
        const year = currentMonth.getFullYear();
        const month = currentMonth.getMonth();
        const firstDay = new Date(year, month, 1).getDay();
        const daysInMonth = new Date(year, month + 1, 0).getDate();

        const cells = [];
        for (let i = 0; i < firstDay; i++) {
            cells.push(<div key={`empty-${i}`} className="pb-day-cell empty"></div>);
        }

        for (let day = 1; day <= daysInMonth; day++) {
            const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            const isAvailable = availability[dateStr] && availability[dateStr].length > 0;
            const isSelected = selectedDate === dateStr;

            cells.push(
                <div 
                    key={day} 
                    className={`pb-day-cell ${isAvailable ? 'available' : ''} ${isSelected ? 'selected' : ''}`}
                    onClick={() => {
                        if (isAvailable) {
                            setSelectedDate(dateStr);
                            setSelectedTime(null);
                        }
                    }}
                >
                    {day}
                </div>
            );
        }

        return (
            <div className="pb-calendar-container">
                <div className="pb-month-header">
                    <button className="pb-month-nav" onClick={prevMonth}><ChevronLeft /></button>
                    <span>{currentMonth.toLocaleString('es-ES', { month: 'long', year: 'numeric' }).toUpperCase()}</span>
                    <button className="pb-month-nav" onClick={nextMonth}><ChevronRight /></button>
                </div>
                <div className="pb-days-grid">
                    {DAYS_OF_WEEK.map(d => <div key={d} className="pb-day-header">{d}</div>)}
                    {cells}
                </div>
            </div>
        );
    };

    if (loading && step === 1) return <div className="public-booking-container">Cargando...</div>;
    if (error) return <div className="public-booking-container">{error}</div>;

    const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    const isStep2Valid = guestName.trim().length > 0 && isValidEmail(guestEmail);
    const isStep3Valid = objective.trim().length > 0;

    const handleKeyDownStep2 = (e) => {
        if (e.key === 'Enter' && isStep2Valid) {
            handleNext();
        }
    };

    const handleKeyDownStep3 = (e) => {
        if (e.key === 'Enter' && !e.shiftKey && isStep3Valid) {
            e.preventDefault();
            handleNext();
        }
    };

    return (
        <div className="public-booking-container">
            <div className="pb-wizard-card">
                
                {step === 1 && (
                    <>
                        <img 
                            src={hostInfo?.image || `https://ui-avatars.com/api/?name=${hostInfo?.name || 'User'}`} 
                            alt="Avatar" 
                            className="pb-avatar" 
                        />
                        <h2 className="pb-name">{hostInfo?.name || 'Usuario'}</h2>
                        <p style={{ color: '#9ca3af', textAlign: 'center', marginBottom: '24px' }}>
                            Agendar una {bookingType} de {duration >= 60 ? `${duration / 60} hora` : `${duration} min`}
                        </p>
                        <button className="pb-btn-primary" onClick={handleNext}>Agendar</button>
                    </>
                )}

                {step === 2 && (
                    <>
                        <h3 className="pb-title">Tus Datos</h3>
                        <div className="pb-form-group">
                            <label className="pb-label">Nombre completo *</label>
                            <input 
                                type="text" 
                                className="pb-input" 
                                value={guestName} 
                                onChange={e => setGuestName(e.target.value)} 
                                onKeyDown={handleKeyDownStep2}
                                placeholder="Ej. Juan Pérez"
                                autoFocus
                            />
                        </div>
                        <div className="pb-form-group">
                            <label className="pb-label">Correo electrónico *</label>
                            <input 
                                type="email" 
                                className={`pb-input ${guestEmail && !isValidEmail(guestEmail) ? 'pb-input-error' : ''}`} 
                                value={guestEmail} 
                                onChange={e => setGuestEmail(e.target.value)} 
                                onKeyDown={handleKeyDownStep2}
                                placeholder="juan@ejemplo.com"
                            />
                            {guestEmail && !isValidEmail(guestEmail) && (
                                <span style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px' }}>Correo inválido</span>
                            )}
                        </div>
                        <button 
                            className="pb-btn-primary" 
                            disabled={!isStep2Valid} 
                            onClick={handleNext}
                        >
                            Siguiente
                        </button>
                    </>
                )}

                {step === 3 && (
                    <>
                        <h3 className="pb-title">¿De qué trata la cita?</h3>
                        <div className="pb-form-group">
                            <label className="pb-label">Objetivo o puntos a tratar *</label>
                            <textarea 
                                className="pb-input" 
                                value={objective} 
                                onChange={e => setObjective(e.target.value)} 
                                onKeyDown={handleKeyDownStep3}
                                placeholder="Detalles de la reunión..."
                                autoFocus
                            />
                        </div>
                        <button 
                            className="pb-btn-primary" 
                            disabled={!isStep3Valid} 
                            onClick={handleNext}
                        >
                            Siguiente
                        </button>
                    </>
                )}

                {step === 4 && (
                    <>
                        <h3 className="pb-title">Selecciona Fecha y Hora</h3>
                        {renderCalendar()}

                        {selectedDate && availability[selectedDate] && (
                            <div style={{ width: '100%', marginTop: '16px' }}>
                                <div className="pb-label" style={{ marginBottom: '4px' }}>Horas disponibles para el {selectedDate}</div>
                                <div style={{ color: '#9ca3af', fontSize: '13px', marginBottom: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                                    <MapPin size={14} /> {Intl.DateTimeFormat().resolvedOptions().timeZone}
                                </div>
                                <div className="pb-times-grid">
                                    {availability[selectedDate].map(time => (
                                        <button 
                                            key={time} 
                                            className={`pb-time-btn ${selectedTime === time ? 'selected' : ''}`}
                                            onClick={() => setSelectedTime(time)}
                                        >
                                            {time}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        <button 
                            className="pb-btn-primary" 
                            style={{ marginTop: '16px' }}
                            disabled={!selectedDate || !selectedTime || loading} 
                            onClick={handleConfirm}
                        >
                            {loading ? 'Agendando...' : 'Confirmar'}
                        </button>
                    </>
                )}

                {step === 5 && (
                    <>
                        <div className="pb-success-icon">
                            <Check color="#ffffff" size={32} />
                        </div>
                        <h2 className="pb-name">¡Agendado!</h2>
                        <p style={{ color: '#9ca3af', lineHeight: '1.5', textAlign: 'center' }}>
                            Quedó agendado para el <strong>{selectedDate}</strong> a las <strong>{selectedTime}</strong>.<br/>
                            <span style={{ fontSize: '13px', opacity: 0.8 }}><MapPin size={12} style={{ display: 'inline', marginBottom: '-2px' }}/> {Intl.DateTimeFormat().resolvedOptions().timeZone}</span><br/><br/>
                            Te enviamos una confirmación por correo.
                        </p>
                    </>
                )}

            </div>
        </div>
    );
};

export default PublicBooking;
